import { useQuery } from '@tanstack/react-query';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
	Box,
	Button,
	Center,
	Flex,
	GifOutline,
	HStack,
	Image,
	Input,
	Link,
	Popover,
	PopoverContent,
	PopoverTrigger,
	Portal,
	rem,
	Spinner,
	Stack,
	Text,
	useDisclosure,
	VStack,
} from '@bonusly/design-system';
import { useHttpClient } from '@bonusly/http-client';

import { useDebounceValue } from '../../../../hooks/use-debounce-value';
import { GifProvider, PROVIDER_CONFIG } from '../../../../lib/gif-provider';
import { useActionInput } from '../../context';
import { useIsEnabled } from '../../hooks/use-is-enabled';
import { AttachmentCategory, Gif, SocialAttachment } from '../../lib/entities';
import { split } from '../../utils/split';
import { DecorationButton } from './decoration-button';

const DEFAULT_HEIGHT = 440;

interface GifButtonProps {
	decorationVariant?: 'default' | 'list-item';
	onChildPopoverChange?: (isOpen: boolean) => void;
}

export const GifButton: FC<GifButtonProps> = ({ decorationVariant = 'default', onChildPopoverChange }) => {
	const { t } = useTranslation('action_input');
	const isEnabled = useIsEnabled('gif');
	const http = useHttpClient();
	const selector = useDisclosure();
	const [inputValue, setInputValue] = useState('');
	const [height, setHeight] = useState(DEFAULT_HEIGHT);
	const query = useDebounceValue(inputValue, 300);

	const suggestions = [1, 2, 3].map((i) => {
		return t(`search_gifs.suggestion_${i}`);
	});

	const { data: { gifs: [left, right] = [[], []], provider = 'tenor' as GifProvider } = {}, isLoading } = useQuery<{
		gifs: [Gif[], Gif[]];
		provider: GifProvider;
	}>({
		queryKey: ['gifs', query],
		queryFn: async () => {
			const res = await http.get('/gifs', { params: { query } });
			const gifs = res.data.result.map((gif: any) => new Gif(gif.id, gif));

			return {
				gifs: split(gifs),
				provider: (res.data.provider || 'tenor') as GifProvider,
			};
		},
		enabled: isEnabled,
	});

	const providerConfig = PROVIDER_CONFIG[provider] || PROVIDER_CONFIG.tenor;

	// The gif selector is probably the largest of the selectors in the bonus box because it needs to fill a large amount of vertical space and it's connected to
	// the button that tends to sit near the center of a user's screen. PopperJS is used under the hood to manage the positioning of the popover and will automatically
	// flip the popover from the bottom to the top if there's isn't enough room below it. However, if there isn't enough room below the button to render the popover
	// there most likely won't be any room above it either. This caused an issue where the popover would get cut off by the bottom of the screen because PopperJS could
	// not find and appropriate place to position it.
	//
	// This open handler now checks if there is enough room either below or above the anchor button and will adjust the height of the popover to fit in the available space
	// if it is less than the default height. This will prevent the popover from getting cut off by the bottom of the screen in most cases.
	const handleOpen = async (e: any) => {
		const top = e.target.getBoundingClientRect().top;
		const bottom = window.innerHeight - top - e.target.clientHeight;

		if (bottom < DEFAULT_HEIGHT) {
			setHeight(Math.max(top, bottom) - 40);
		} else {
			setHeight(DEFAULT_HEIGHT);
		}

		selector.onToggle();
		onChildPopoverChange?.(true);
	};

	const handleClose = () => {
		selector.onClose();
		setInputValue('');
		onChildPopoverChange?.(false);
	};

	if (!isEnabled) {
		return null;
	}

	return (
		<Popover isLazy isOpen={selector.isOpen} onClose={handleClose} placement="bottom" strategy="fixed">
			<PopoverTrigger>
				<DecorationButton
					label={t('decorations.gif_tooltip')}
					onClick={(e) => {
						void handleOpen(e);
					}}
					decorationVariant={decorationVariant}
				>
					<GifOutline />
				</DecorationButton>
			</PopoverTrigger>

			<Portal>
				<PopoverContent w={{ base: '100vw', sm: 'min-content' }} maxW="100vw">
					<Stack position="relative" h={`${height}px`} w={{ base: '100vw', sm: 'min-content' }}>
						<VStack spacing={3} p={5} pb={3}>
							<Input
								type="text"
								onChange={(e) => setInputValue(e.target.value)}
								placeholder={t('search_gifs.placeholder_provider', { provider: providerConfig.name })}
								value={inputValue}
							/>

							<HStack>
								{suggestions.map((suggestion, i) => (
									<Button key={i} colorScheme="secondary" variant="ghost" onClick={() => setInputValue(suggestion)}>
										{suggestion}
									</Button>
								))}
							</HStack>
						</VStack>

						{!isLoading && (
							<Flex gap={rem(1)} flexGrow={1} overflow="hidden" overflowY="scroll" role="listbox" borderBottomRadius="md">
								<GifStack gifs={left} onClose={handleClose} />
								<GifStack gifs={right} onClose={handleClose} />
							</Flex>
						)}
						{isLoading && (
							<Center p={2} h="100%">
								<Spinner />
							</Center>
						)}

						<Box position="absolute" bottom={2} right={3} bg="blackAlpha.700" px={2} py={1} borderRadius="md">
							<Text fontSize="xs" color="whiteAlpha.800">
								{t('search_gifs.powered_by')}{' '}
								<Link href={providerConfig.url} isExternal fontWeight="semibold" color="white">
									{providerConfig.name}
								</Link>
							</Text>
						</Box>
					</Stack>
				</PopoverContent>
			</Portal>
		</Popover>
	);
};

interface GifStackProps {
	gifs: Gif[];
	onClose: VoidFunction;
}

const GifStack: FC<GifStackProps> = ({ gifs, onClose }) => {
	const { setAttachment: setActionAttachment, attachment: existingAttachment } = useActionInput();
	const { t } = useTranslation('action_input');

	const handleClick = (newAttachment: SocialAttachment) => {
		// eslint-disable-next-line
		if (existingAttachment?.category === AttachmentCategory.Video && !confirm(t('decorations.upload_gif_warning'))) return;
		setActionAttachment(newAttachment);
		onClose();
	};

	return (
		<VStack spacing={rem(1)}>
			{gifs.map((gif) => (
				<Box as="button" key={gif.gif} role="option" onClick={() => handleClick(gif.toAttachment())}>
					<Image alt={gif.content_description} src={gif.preview} />
				</Box>
			))}
		</VStack>
	);
};
